import { useApolloClient } from "@apollo/client";
import { Modal, ModalOpenButton } from "@msys/ui";
import { Cancel as CancelIcon } from "@mui/icons-material";
import { Button, DialogContentText, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Form, Formik } from "formik";
import { uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import * as Yup from "yup";
import { TextField } from "../../../commons/form-fields/TextField.js";
import { useCancelInvoiceMutation } from "../Invoices.generated.js";

interface Props {
  invoiceId: string;
  projectId: string;
  docId: string;
}

export const CancelInvoiceButton = ({ invoiceId, projectId, docId }: Props) => {
  const client = useApolloClient();
  const [cancelInvoice, { loading }] = useCancelInvoiceMutation({ client });

  const { enqueueSnackbar } = useSnackbar();

  const { t } = useTranslate("InvoiceDetailsForm");

  const handleSubmit = async (values: FormValues) => {
    try {
      await cancelInvoice({
        variables: {
          input: {
            projectId,
            docId,
            invoiceId,
            values: {
              reason: values.reason,
            },
          },
        },
      });
      enqueueSnackbar(t("Invoice cancelled"));
    } catch (e) {
      if (e instanceof Error) enqueueSnackbar(e.message, { variant: "error" });
    }
  };

  return (
    <ModalOpenButton
      Modal={CancelInvoiceModal}
      modalProps={{
        handleComplete: handleSubmit,
      }}
    >
      <Button
        color="secondary"
        variant="outlined"
        loading={loading}
        startIcon={<CancelIcon />}
      >
        {t("Cancel Invoice")}
      </Button>
    </ModalOpenButton>
  );
};

interface FormValues {
  reason: string;
}

function CancelInvoiceModal({
  handleClose,
  handleComplete,
}: {
  handleClose: () => void;
  handleComplete: (value: FormValues) => Promise<void>;
}) {
  const { t } = useTranslate(["Global", "InvoiceDetailsForm", "Invoices"]);

  const formId = React.useMemo(() => uniqueId(), []);

  return (
    <Formik<FormValues>
      initialValues={{
        reason: "",
      }}
      validationSchema={Yup.object().shape({
        reason: Yup.string()
          .label(
            t("Cancellation reason", {
              ns: "Invoices",
            })
          )
          .max(200),
      })}
      onSubmit={handleComplete}
    >
      {formikProps => (
        <Modal
          id="cancel-invoice-modal"
          title={t("Cancel Invoice", {
            ns: "InvoiceDetailsForm",
          })}
          dialogProps={{ maxWidth: "xs" }}
          handleClose={handleClose}
          actionButtons={[
            {
              label: t("Cancel", {
                ns: "Global",
              }),
              handleClick: handleClose,
              buttonProps: {
                variant: "text",
                disabled: formikProps.isSubmitting,
              },
            },
            {
              label: t("Save", {
                ns: "Global",
              }),
              buttonProps: {
                form: formId,
                type: "submit",
                disabled: !formikProps.isValid,
                loading: formikProps.isSubmitting,
              },
            },
          ]}
        >
          <DialogContentText>
            {t("Enter the reason for invoice cancellation (optional):", {
              ns: "InvoiceDetailsForm",
            })}
          </DialogContentText>
          <Form id={formId}>
            <Stack spacing={1}>
              <TextField
                label={t("Cancellation reason", {
                  ns: "Invoices",
                })}
                rows={3}
                name="reason"
                multiline
              />
            </Stack>
          </Form>
        </Modal>
      )}
    </Formik>
  );
}
